import React, { useState } from 'react';

const EmployeeDocs = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: '',
  });
  const [documents, setDocuments] = useState([
    { id: 1, name: 'Aadhaar Card.pdf', type: 'id', uploadDate: '2023-05-15', expiryDate: null },
    { id: 2, name: 'PAN Card.pdf', type: 'id', uploadDate: '2023-05-15', expiryDate: null },
    { id: 3, name: 'Experience Certificate.pdf', type: 'certificate', uploadDate: '2023-05-20', expiryDate: null },
    { id: 4, name: 'Appointment Letter.pdf', type: 'employment', uploadDate: '2023-05-10', expiryDate: '2025-05-10' },
    { id: 5, name: 'Visa Document.pdf', type: 'legal', uploadDate: '2023-06-01', expiryDate: '2024-06-01' },
  ]);

  const filteredDocs = activeTab === 'all' 
    ? documents 
    : documents.filter(doc => doc.type === activeTab);

  const documentTypes = [
    { id: 'all', name: 'All Documents' },
    { id: 'id', name: 'ID Proof' },
    { id: 'certificate', name: 'Certificates' },
    { id: 'employment', name: 'Employment Docs' },
    { id: 'legal', name: 'Legal Docs' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDocument({
      ...newDocument,
      [name]: value
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewDocument({
        ...newDocument,
        name: file.name
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newDoc = {
      id: documents.length + 1,
      name: newDocument.name,
      type: 'general', // Default type since we removed the type selection
      uploadDate: new Date().toISOString().split('T')[0],
      expiryDate: null // Default to null since we removed the expiry date field
    };
    
    setDocuments([...documents, newDoc]);
    setShowUploadModal(false);
    setNewDocument({
      name: '',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-indigo-800 mb-2">Employee Docs 📂</h1>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto">
            For storing employee-specific documents. Secure storage with access limited to HR/admin and the employee.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-indigo-100">
            <div className="text-indigo-600 text-2xl mb-3">📤</div>
            <h3 className="text-xl font-semibold text-indigo-800 mb-2">Upload Documents</h3>
            <p className="text-indigo-600">Upload employee documents at onboarding (ID proof, certificates, appointment letter, etc.)</p>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border border-indigo-100">
            <div className="text-indigo-600 text-2xl mb-3">💼</div>
            <h3 className="text-xl font-semibold text-indigo-800 mb-2">Work Documents</h3>
            <p className="text-indigo-600">Store work-related documents (performance letters, promotion letters, etc.)</p>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border border-indigo-100">
            <div className="text-indigo-600 text-2xl mb-3">⏰</div>
            <h3 className="text-xl font-semibold text-indigo-800 mb-2">Track Expiry</h3>
            <p className="text-indigo-600">Track expiry of important documents (e.g., passport, visa, contract)</p>
          </div>
        </div>

        {/* Example Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white mb-10 shadow-xl">
          <h2 className="text-2xl font-bold mb-4">Example for Employee Amit:</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Aadhaar Card.pdf', 'PAN Card.pdf', 'Experience Certificate.pdf', 'Appointment Letter.pdf'].map((doc, index) => (
              <div key={index} className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">📄</div>
                <p className="text-sm font-medium">{doc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Document Browser */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-indigo-800">Document Repository</h2>
            <button 
              onClick={() => setShowUploadModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              Upload New Document
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
            {documentTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setActiveTab(type.id)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition duration-200 ${
                  activeTab === type.id 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type.name}
              </button>
            ))}
          </div>

          {/* Documents List */}
          <div className="space-y-4">
            {filteredDocs.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="flex items-center">
                  <div className="text-indigo-600 text-2xl mr-4">📄</div>
                  <div>
                    <h3 className="font-medium text-indigo-800">{doc.name}</h3>
                    <p className="text-sm text-indigo-600">Uploaded: {doc.uploadDate}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  {doc.expiryDate && (
                    <div className="text-sm bg-amber-100 text-amber-800 px-3 py-1 rounded-full">
                      Expires: {doc.expiryDate}
                    </div>
                  )}
                  <button className="text-indigo-600 hover:text-indigo-800">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button className="text-indigo-600 hover:text-indigo-800">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-6 text-center text-indigo-700 text-sm">
          <p>🔒 Secure: Only HR/admin and the specific employee can view these documents</p>
        </div>
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-indigo-800">Upload New Document</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document File</label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
                <input
                  type="text"
                  name="name"
                  value={newDocument.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter document name"
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium"
                >
                  Upload Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDocs;
